import React from 'react';
import { ArrowRight, Clock, Shield, Users } from 'lucide-react';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="pt-28 md:pt-36 bg-gradient-to-br from-navy-50 to-primary-50 min-h-screen flex items-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="block text-navy-900">InTimelog — Airline <span className="whitespace-nowrap">Crew Transportation</span></span>
                <span className="block text-primary-600">Always On Time.</span>
              </h1>
              <p className="text-xl text-navy-600 leading-relaxed">
                InTimelog is a Dallas, Texas–based transportation company specializing in airline crew logistics while supporting hotels, corporate clients, and airport operations across the DFW area.
              </p>
              <p className="text-lg text-navy-600 leading-relaxed">
                Powered by real-time coordination and a commitment to precision, we deliver reliable, professional transportation designed for on-time performance in every movement.
              </p>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-start space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg flex-shrink-0">
                  <Clock className="w-5 h-5 text-primary-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-navy-900 leading-tight whitespace-nowrap">Always On Time</h3>
                  <p className="text-sm text-navy-600 mt-1">Punctual service</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg flex-shrink-0">
                  <Shield className="w-5 h-5 text-primary-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-navy-900 leading-tight whitespace-nowrap">Fully Licensed</h3>
                  <p className="text-sm text-navy-600 mt-1">Insured & certified</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg flex-shrink-0">
                  <Users className="w-5 h-5 text-primary-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-navy-900 leading-tight">Pro Drivers</h3>
                  <p className="text-sm text-navy-600 mt-1">Experienced team</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToContact}
                className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
              >
                Contact Us
              </button>
              <button
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 text-primary-600 font-semibold rounded-lg hover:bg-primary-600 hover:text-white transition-all duration-200"
              >
                Learn More
              </button>
            </div>
          </div>

          {/* Image/Visual */}
          <div className="relative">
            <div className="bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl p-8 shadow-2xl">
              <div className="bg-white rounded-lg p-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-navy-700">Real-Time Trip Management</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="text-sm text-navy-700 font-medium">DFW Terminal D → Marriott</span>
                      <div className="text-xs text-navy-500">XY1847 • 08:45</div>
                    </div>
                    <span className="text-xs text-green-600 font-medium">Picked Up</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="text-sm text-navy-700 font-medium">Hyatt Regency → DFW Terminal C</span>
                      <div className="text-xs text-navy-500">AB2943 • 10:15</div>
                    </div>
                    <span className="text-xs text-blue-600 font-medium">Ready for Pickup</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="text-sm text-navy-700 font-medium">Hilton DFW → DFW Terminal E</span>
                      <div className="text-xs text-navy-500">ZQ5129 • 11:30</div>
                    </div>
                    <span className="text-xs text-amber-600 font-medium">Assigned</span>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center justify-between text-xs text-navy-600">
                    <span>12 Active Drivers</span>
                    <span>18 Trips Today</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Stats */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">99.8%</div>
                <div className="text-xs text-navy-600">On-Time Performance</div>
              </div>
            </div>
            
            <div className="absolute -top-6 -right-6 bg-white rounded-lg shadow-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">24/7</div>
                <div className="text-xs text-navy-600">Service</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;